import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../../identity/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { employeeOnboardingGateway } from '../index';
import { formatDisplayDate } from '../format';
import { QuestionsContext, type QuestionsApi } from './QuestionsContext';
import { QUESTION_MAX_LENGTH, questionFieldLabel, type EmployeeQuestion, type QuestionFieldKey } from './questionFields';

/**
 * "Ask about this": wraps the onboarding pages so any read-only employer-owned field can open a question box. The question goes to the
 * administrator who invited the employee (by email, server side); this component never sees or shows an address. Replies appear here.
 * A failed read of earlier questions is not an error for the page: the Ask buttons still work.
 */
export function QuestionsProvider({ children }: { children: ReactNode }) {
  const [questions, setQuestions] = useState<EmployeeQuestion[]>([]);
  const [openKey, setOpenKey] = useState<QuestionFieldKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    employeeOnboardingGateway.listMyQuestions()
      .then((items) => { if (!cancelled) setQuestions(items); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const api = useMemo<QuestionsApi>(() => ({
    forField: (key) => questions.filter((q) => q.fieldKey === key),
    ask: (key) => setOpenKey(key),
  }), [questions]);

  const onSent = useCallback((q: EmployeeQuestion) => setQuestions((prev) => [q, ...prev]), []);

  return (
    <QuestionsContext.Provider value={api}>
      {children}
      {openKey && <QuestionDialog fieldKey={openKey} history={questions.filter((q) => q.fieldKey === openKey)} onSent={onSent} onClose={() => setOpenKey(null)} />}
    </QuestionsContext.Provider>
  );
}

const FOCUSABLE = 'button:not([disabled]), textarea:not([disabled]), [href]';

function QuestionDialog({ fieldKey, history, onSent, onClose }: {
  fieldKey: QuestionFieldKey; history: EmployeeQuestion[]; onSent: (q: EmployeeQuestion) => void; onClose: () => void;
}) {
  const uid = useId();
  const ref = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const label = questionFieldLabel(fieldKey);

  useEffect(() => {
    const opener = openerRef.current;
    ref.current?.querySelector<HTMLElement>('textarea')?.focus();
    return () => { if (opener instanceof HTMLElement) opener.focus(); };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const text = message.trim();
    if (!text) {
      setError('Please write your question.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      onSent(await employeeOnboardingGateway.askQuestion(fieldKey, text));
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'question_limit_reached') {
        setError('You have reached the limit of questions for today. Please try again tomorrow, or contact your administrator directly.');
      } else {
        setError(friendlyErrorMessage(err, 'We could not send your question. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  const titleId = `${uid}-title`;
  return (
    <div className="eo-modal-backdrop" role="presentation">
      <div ref={ref} className="eo-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
        <h2 id={titleId} className="eo-modal__title">Ask about {label}</h2>
        {sent ? (
          <div role="status">
            <p>Your question was sent to the person who invited you. You will see their reply here.</p>
            <div className="eo-modal__actions"><button type="button" className="eo-btn eo-btn--primary" onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} noValidate>
            <p className="eo-note">Your employer sets this value. Do not include passwords, bank details or your full SSN in your question.</p>
            {history.length > 0 && (
              <ul className="eo-qa" aria-label={`Earlier questions about ${label}`}>
                {history.map((q) => (
                  <li key={q.id}>
                    <p className="eo-qa__q">You asked on {formatDisplayDate(q.createdAtUtc)}: {q.message}</p>
                    {q.reply
                      ? <p className="eo-qa__a">Reply{q.repliedAtUtc ? ` on ${formatDisplayDate(q.repliedAtUtc)}` : ''}: {q.reply}</p>
                      : <p className="eo-qa__pending">Waiting for a reply.</p>}
                  </li>
                ))}
              </ul>
            )}
            <label htmlFor={`${uid}-msg`} className="eo-field__label">Your question</label>
            <textarea
              id={`${uid}-msg`}
              className="eo-input eo-textarea"
              rows={5}
              maxLength={QUESTION_MAX_LENGTH}
              value={message}
              aria-invalid={error ? true : undefined}
              aria-describedby={`${uid}-count${error ? ` ${uid}-err` : ''}`}
              onChange={(e) => { setMessage(e.target.value); setError(null); }}
            />
            <p id={`${uid}-count`} className="eo-field__hint">{message.length} / {QUESTION_MAX_LENGTH}</p>
            {error && <p id={`${uid}-err`} className="eo-field__error" role="alert">{error}</p>}
            <div className="eo-modal__actions">
              <button type="button" className="eo-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="eo-btn eo-btn--primary" disabled={busy}>{busy ? 'Sending…' : 'Send question'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
